import { useEffect, useState } from "react";

/**
 * Reactive hook for the user's `prefers-reduced-motion` media query.
 *
 * Returns `true` when the OS-level setting requests reduced motion.
 * Components should suppress decorative animations (pulses, parallax,
 * particle effects) when this returns `true`.
 *
 * SSR-safe. In the browser the initial state is resolved synchronously
 * from `window.matchMedia` so downstream effects (parallax scroll
 * listener attach, success-burst mount lifecycle) never observe a
 * one-tick `false → true` flicker that would attach work the user
 * explicitly opted out of.
 *
 * Originally lived inside `components/AuthScene.tsx`; extracted here so
 * non-auth surfaces (e.g. the marketing homepage's lazy fallback, the
 * Task #468 next-gen micro-interaction primitives in
 * `lib/usePrefersReducedMotion.ts`, which re-exports from here) can
 * import it without coupling to the heavy AuthScene module. AuthScene
 * still re-exports this symbol for backward compatibility with existing
 * call sites (sign-in, reset-password, SoftTour, etc.).
 */
export function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefers(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return prefers;
}
